import React, { useState } from 'react';
import { 
  Box, Grid, Paper, Typography, Stack, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Chip,
  Tooltip, TextField, Button
} from '@mui/material';
import { 
  Verified as VerifiedIcon,
  InfoOutlined as InfoIcon,
  Bolt as ActionIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const StaffPayroll = () => {
  const [dateRange, setDateRange] = useState({ start: '', end: '' });

  return (
    <Box component={motion.div} initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
      
      {/* --- Section 1: Ultra-Compact Header (Title Removed) --- */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 1.2, mb: 2, borderRadius: 3, 
          background: 'rgba(255, 255, 255, 0.8)', 
          backdropFilter: 'blur(10px)',
          border: '1px solid #e2e8f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" sx={{ flexGrow: 1 }}>
          <TextField
            label="From"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
            sx={{ 
              width: 160, 
              '& .MuiInputBase-root': { bgcolor: '#fff', borderRadius: 2, height: 38, fontSize: '0.8rem' } 
            }}
          />
          <Typography variant="caption" fontWeight={900} color="#cbd5e1">TO</Typography>
          <TextField
            label="To"
            type="date"
            size="small"
            InputLabelProps={{ shrink: true }}
            onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
            sx={{ 
              width: 160, 
              '& .MuiInputBase-root': { bgcolor: '#fff', borderRadius: 2, height: 38, fontSize: '0.8rem' } 
            }}
          />
        </Stack>

        <Button
          variant="contained"
          disableElevation
          startIcon={<ActionIcon sx={{ fontSize: 18 }} />}
          sx={{ 
            height: 38, px: 3, borderRadius: 2, fontWeight: 800, fontSize: '0.75rem',
            background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
            whiteSpace: 'nowrap',
            textTransform: 'none',
            '&:hover': { background: '#0f172a' }
          }}
        >
          Authorize & Run Payroll
        </Button>
      </Paper>

      {/* --- Section 2: Space-Friendly Summary Snapshot --- */}
      <Grid container spacing={1.5} sx={{ mb: 2 }}>
        {[
          { label: 'Total Payable', value: '£28,450.00', color: '#1e293b' },
          { label: 'Verified Hours', value: '1,240.5h', color: '#6366f1' },
          { label: 'Pending Adjustments', value: '12 Shifts', color: '#f59e0b' }
        ].map((item, i) => (
          <Grid item xs={12} md={4} key={i}>
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 1.5, 
                borderRadius: 2.5, 
                bgcolor: '#fff', 
                border: '1px solid #e2e8f0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Box>
                <Typography variant="caption" sx={{ fontWeight: 800, color: '#64748b', textTransform: 'uppercase', fontSize: '0.65rem' }}>
                  {item.label}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 950, color: item.color, lineHeight: 1.2 }}>
                  {item.value}
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 700, fontSize: '0.6rem' }}>
                {dateRange.start ? `${dateRange.start} - ${dateRange.end}` : 'THIS PERIOD'}
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* --- Section 3: Payroll Table --- */}
      <TableContainer 
        component={Paper} 
        elevation={0} 
        sx={{ 
          borderRadius: 3, 
          border: '1px solid #e2e8f0',
          background: '#fff',
          overflow: 'hidden'
        }}
      >
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 800, color: '#475569', fontSize: '0.75rem', py: 2 } }}>
              <TableCell>Staff Member</TableCell>
              <TableCell>Shift Breakdown</TableCell>
              <TableCell>Hourly Rates</TableCell>
              <TableCell>Deductions/Bonus</TableCell>
              <TableCell sx={{ bgcolor: '#f1f5f9 !important' }}>Net Pay</TableCell>
              <TableCell align="right">Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {[
              { 
                id: 1, name: 'Sarah Jenkins', role: 'Senior Carer', 
                basic: '140h', holiday: '12h', rate: '£14.50', bankHolidyRate: '£21.75',
                travel: '£45.00', net: '2,245.50', status: 'Ready' 
              },
              { 
                id: 2, name: 'James Wilson', role: 'Healthcare Assistant', 
                basic: '120h', holiday: '0h', rate: '£12.00', bankHolidyRate: '£18.00',
                travel: '£30.00', net: '1,470.00', status: 'Flagged' 
              }
            ].map((staff) => (
              <TableRow key={staff.id} hover sx={{ '& td': { py: 1.2 } }}>
                <TableCell>
                  <Typography variant="body2" fontWeight={800}>{staff.name}</Typography>
                  <Typography variant="caption" color="primary" fontWeight={700}>{staff.role}</Typography>
                </TableCell>

                <TableCell>
                  <Stack spacing={0.2}>
                    <Stack direction="row" justifyContent="space-between" sx={{ width: 110 }}>
                      <Typography variant="caption" color="textSecondary">Base:</Typography>
                      <Typography variant="caption" fontWeight={700}>{staff.basic}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" sx={{ width: 110 }}>
                      <Typography variant="caption" color="textSecondary">Hol:</Typography>
                      <Typography variant="caption" fontWeight={700} color="secondary.main">{staff.holiday}</Typography>
                    </Stack>
                  </Stack>
                </TableCell>

                <TableCell>
                  <Typography variant="caption" display="block">Base: <b>{staff.rate}</b></Typography>
                  <Typography variant="caption" display="block" sx={{ color: '#059669' }}>BH: <b>{staff.bankHolidyRate}</b></Typography>
                </TableCell>

                <TableCell>
                  <Typography variant="caption" display="block" color="textSecondary">Travel: <b>+{staff.travel}</b></Typography>
                  <Typography variant="caption" display="block" color="error">NI: <b>Calculated</b></Typography>
                </TableCell>

                <TableCell sx={{ bgcolor: '#f8fafc' }}>
                  <Typography variant="body2" fontWeight={950} color="#1e293b">
                    £{staff.net}
                  </Typography>
                </TableCell>

                <TableCell align="right">
                  {staff.status === 'Ready' ? (
                    <Chip label="Ready" size="small" sx={{ bgcolor: '#ecfdf5', color: '#065f46', fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
                  ) : (
                    <Tooltip title="Missing Checkout Time">
                      <Chip label="Flagged" size="small" sx={{ bgcolor: '#fff7ed', color: '#9a3412', fontWeight: 800, fontSize: '0.65rem', height: 20 }} />
                    </Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 2, p: 1.5, borderRadius: 2, bgcolor: '#f1f5f9', borderLeft: '4px solid #6366f1' }}>
        <Typography variant="caption" display="block" sx={{ color: '#475569', fontWeight: 600, fontSize: '0.65rem' }}>
          * Shifts completed within the selected range are audited against GPS logs.
        </Typography>
      </Box>
    </Box>
  );
};

export default StaffPayroll;